import { createBrowserRouter, Navigate, Outlet } from 'react-router-dom'
import RootRedirect from '@/pages/RootRedirect'
import EditorPage from '@/pages/editor/EditorPage'
import LoginPage from '@/pages/auth/LoginPage'
import RegisterPage from '@/pages/auth/RegisterPage'
import VerifyEmailPage from '@/pages/auth/VerifyEmailPage'
import ForgotPasswordPage from '@/pages/auth/ForgotPasswordPage'
import ResetPasswordPage from '@/pages/auth/ResetPasswordPage'
import CreateWorkspacePage from '@/pages/workspace/CreateWorkspacePage'
import ProjectListPage from '@/pages/projects/ProjectListPage'
import WorkspacesListPage from '@/pages/workspace/WorkspacesListPage'
import ProfilePage from '@/pages/profile/ProfilePage'
import ManagerPage from '@/pages/workspace/ManagerPage'
import SubscriptionPage from '@/pages/billing/SubscriptionPage'
import RiskPage from '@/pages/admin/RiskPage'
import AccountPage from '@/pages/admin/AccountPage'
import MetricsPage from '@/pages/admin/MetricsPage'
import PlansPage from '@/pages/admin/PlansPage'
import AnnouncementsPage from '@/pages/admin/AnnouncementsPage'
import UsersPage from '@/pages/admin/UsersPage'
import UserDetailPage from '@/pages/admin/UserDetailPage'
import { AdminLayout } from '@/components/admin/AdminLayout'
import { RootLayout } from '@/components/layout/RootLayout'
import { AuthedLayout } from '@/components/layout/AuthedLayout'
import { RequireAuth, RequireGuest, RequireStaff, RequireVerifiedEmail, RequireWorkspace } from './guards'

export const router = createBrowserRouter([
  {
    element: <RootLayout />,
    children: [
      {
        path: '/login',
        element: (
          <RequireGuest>
            <LoginPage />
          </RequireGuest>
        ),
      },
      {
        path: '/register',
        element: (
          <RequireGuest>
            <RegisterPage />
          </RequireGuest>
        ),
      },
      {
        path: '/forgot-password',
        element: (
          <RequireGuest>
            <ForgotPasswordPage />
          </RequireGuest>
        ),
      },
      {
        path: '/reset-password',
        element: (
          <RequireGuest>
            <ResetPasswordPage />
          </RequireGuest>
        ),
      },
      {
        // Outside AuthedLayout on purpose: confirming the address is still part of
        // the sign-up flow, not of the product shell.
        path: '/verify-email',
        element: (
          <RequireAuth>
            <VerifyEmailPage />
          </RequireAuth>
        ),
      },
      {
        element: (
          <RequireAuth>
            <AuthedLayout />
          </RequireAuth>
        ),
        children: [
          {
            // Reachable while unconfirmed: someone who mistyped their address has to
            // be able to fix it, otherwise the account is bricked.
            path: '/profile',
            handle: { crumb: 'Perfil' },
            element: <ProfilePage />,
          },
          {
            element: (
              <RequireVerifiedEmail>
                <Outlet />
              </RequireVerifiedEmail>
            ),
            children: [
              { path: '/', element: <RootRedirect /> },
              {
                element: (
                  <RequireStaff>
                    <AdminLayout />
                  </RequireStaff>
                ),
                children: [
                  { path: '/admin', element: <RiskPage /> },
                  { path: '/admin/accounts/:workspaceId', element: <AccountPage /> },
                  { path: '/admin/metrics', element: <MetricsPage /> },
                  { path: '/admin/plans', element: <PlansPage /> },
                  { path: '/admin/users', element: <UsersPage /> },
                  { path: '/admin/users/:userId', element: <UserDetailPage /> },
                  { path: '/admin/announcements', element: <AnnouncementsPage /> },
                ],
              },
              {
                path: '/workspace/create',
                handle: { crumb: 'navigation.breadcrumbs.newWorkspace' },
                element: <CreateWorkspacePage />,
              },
              {
                path: '/workspaces',
                handle: { crumb: 'workspaces.title' },
                element: <WorkspacesListPage />,
              },
              {
                handle: {
                  crumb: 'Workspaces',
                },
                children: [
                  {
                    path: '/workspace/:workspaceSlug/projects',
                    handle: {
                      crumb: ({ params }: { params: Record<string, string | undefined> }) => params.workspaceSlug ?? 'Workspace',
                    },
                    children: [
                      {
                        index: true,
                        handle: { crumb: 'navigation.breadcrumbs.projects' },
                        element: (
                          <RequireWorkspace>
                            <ProjectListPage />
                          </RequireWorkspace>
                        ),
                      },
                    ],
                  },
                  {
                    path: '/workspace/:workspaceSlug/manager',
                    handle: {
                      crumb: ({ params }: { params: Record<string, string | undefined> }) => params.workspaceSlug ?? 'Workspace',
                    },
                    children: [
                      {
                        index: true,
                        handle: { crumb: 'Gerenciar workspace' },
                        element: (
                          <RequireWorkspace>
                            <ManagerPage />
                          </RequireWorkspace>
                        ),
                      },
                    ],
                  },
                  {
                    path: '/workspace/:workspaceSlug/subscription',
                    handle: {
                      crumb: ({ params }: { params: Record<string, string | undefined> }) => params.workspaceSlug ?? 'Workspace',
                    },
                    children: [
                      {
                        index: true,
                        handle: { crumb: 'billing.title' },
                        element: (
                          <RequireWorkspace>
                            <SubscriptionPage />
                          </RequireWorkspace>
                        ),
                      },
                    ],
                  },
                  {
                    path: '/workspace/:workspaceSlug/projects/:pid/editor',
                    handle: {
                      crumb: ({ params }: { params: Record<string, string | undefined> }) => params.workspaceSlug ?? 'Workspace',
                    },
                    children: [
                      {
                        index: true,
                        handle: { crumb: 'navigation.breadcrumbs.editor' },
                        element: (
                          <RequireWorkspace>
                            <EditorPage />
                          </RequireWorkspace>
                        ),
                      },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
      { path: '*', element: <Navigate to="/" replace /> },
    ],
  },
])
